import React, { useCallback, useEffect, useState } from "react";
import { Handshake, Plus, Search, Loader2, RefreshCw, Percent, Banknote, Trash2, Pencil, ChevronDown, ChevronUp, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { AssociateStatementButton } from "@/components/admin/AssociateStatementButton"; // lot 711 — relevé mensuel PDF

// Lot 710 — Admin › Finances › Associés : parts (%) du revenu NET de la plateforme, gains potentiels mois par mois
// (même formule que la carte « Revenu net » : revenus − frais − TVA stores − dépenses − remboursements), versements et solde.
export const money = (n, c = "EUR") => `${(Math.round((n || 0) * 100) / 100).toLocaleString("fr-FR")} ${c === "EUR" ? "€" : c === "USD" ? "$" : c}`;
const pctFmt = (p) => `${Number(p || 0).toLocaleString("fr-FR")} %`;
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("fr-FR", { month: "short", year: "numeric" });
const initials = (name) => (name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

const PoolCard = ({ data }) => {
  const neg = (data.pool?.net_total || 0) < 0;
  return (
    <div className="yalola-card p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="associates-pool">
      {[
        ["Revenu net cumulé", money(data.pool?.net_total, data.currency), neg ? "#B91C1C" : "var(--yalola-primary)", "associates-pool-total"],
        ["Revenu net ce mois", money(data.pool?.net_month, data.currency), (data.pool?.net_month || 0) < 0 ? "#B91C1C" : "var(--yalola-text)", "associates-pool-month"],
        ["Parts attribuées", pctFmt(data.total_pct), "#8A5A12", "associates-pool-pct"],
        ["Parts disponibles", pctFmt(data.remaining_pct), "#1B7A3D", "associates-pool-remaining"],
      ].map(([l, v, c, id]) => (
        <div key={id} className="rounded-xl p-3" style={{ background: "var(--yalola-surface-alt)" }}>
          <div className="text-[10px] uppercase font-bold tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{l}</div>
          <div className="text-lg font-extrabold mt-0.5" style={{ color: c }} data-testid={id}>{v}</div>
        </div>
      ))}
    </div>
  );
};

// Lot 758 — `readOnly` : carte vue par l'associé lui-même (aucun bouton d'action, pas de suppression de versement).
export const AssociateRow = ({ a, currency, onEdit, onRemove, onPayout, onDeletePayout, readOnly = false }) => {
  const [open, setOpen] = useState(false);
  const u = a.user || {};
  const bal = a.balance || 0;
  return (
    <div className="yalola-card p-4 space-y-2" data-testid={`associate-row-${a.id}`} data-user={a.user_id}>
      <div className="flex items-center gap-3 flex-wrap">
        <Avatar className="h-10 w-10"><AvatarImage src={u.avatar_url} alt="" /><AvatarFallback>{initials(u.name)}</AvatarFallback></Avatar>
        <div className="min-w-0">
          <div className="font-semibold truncate" data-testid={`associate-name-${a.id}`}>{u.name || "Membre"}</div>
          <div className="text-xs truncate" style={{ color: "var(--yalola-text-muted)" }}>{u.email || u.phone || "—"} · depuis {monthLabel(a.since)}</div>
        </div>
        <span className="chip text-xs font-bold flex items-center gap-1 ml-auto" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid={`associate-pct-${a.id}`}><Percent size={11} /> {pctFmt(a.pct)}</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
        {[
          ["Gains potentiels", money(a.earned_total, currency), (a.earned_total || 0) < 0 ? "#B91C1C" : "var(--yalola-primary)", "earned"],
          ["Ce mois", money(a.earned_month, currency), (a.earned_month || 0) < 0 ? "#B91C1C" : "var(--yalola-text)", "month"],
          ["Déjà versé", money(a.paid_total, currency), "#6B7280", "paid"],
          ["Solde à verser", money(bal, currency), bal < 0 ? "#B91C1C" : "#1B7A3D", "balance"],
        ].map(([l, v, c, k]) => (
          <div key={k} className="rounded-lg px-3 py-2" style={{ background: "var(--yalola-surface-alt)" }}>
            <div className="text-[10px] uppercase font-bold tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{l}</div>
            <div className="font-extrabold" style={{ color: c }} data-testid={`associate-${k}-${a.id}`}>{v}</div>
          </div>
        ))}
      </div>
      {a.note && <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{a.note}</p>}
      <div className="flex flex-wrap gap-2">
        {!readOnly && <Button size="sm" variant="outline" className="rounded-full" onClick={() => onEdit(a)} data-testid={`associate-edit-${a.id}`}><Pencil size={13} className="mr-1" /> Modifier la part</Button>}
        {!readOnly && <Button size="sm" variant="outline" className="rounded-full" onClick={() => onPayout(a)} data-testid={`associate-payout-${a.id}`}><Banknote size={13} className="mr-1" /> Enregistrer un versement</Button>}
        {!readOnly && <Button size="sm" variant="outline" className="rounded-full" style={{ color: "#B91C1C" }} onClick={() => onRemove(a)} data-testid={`associate-remove-${a.id}`}><Trash2 size={13} className="mr-1" /> Retirer</Button>}
        <AssociateStatementButton associateId={a.id} since={a.since} testId={`associate-statement-${a.id}`} />
        <button type="button" className="text-xs underline ml-auto flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }} onClick={() => setOpen((o) => !o)} data-testid={`associate-details-${a.id}`}>
          {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Détail par mois & versements
        </button>
      </div>
      {open && (
        <div className="grid md:grid-cols-2 gap-3 text-xs" data-testid={`associate-detail-${a.id}`}>
          <div>
            <div className="font-semibold mb-1">Mois par mois</div>
            {(a.months || []).slice().reverse().map((m) => (
              <div key={m.month} className="flex justify-between py-0.5 border-b" style={{ borderColor: "var(--yalola-border)" }} data-testid={`associate-month-${a.id}-${m.month}`}>
                <span className="capitalize">{monthLabel(m.month)}</span><span style={{ color: "var(--yalola-text-muted)" }}>net {money(m.net, currency)} × {pctFmt(m.pct)}</span><b style={{ color: m.share < 0 ? "#B91C1C" : "inherit" }}>{money(m.share, currency)}</b>
              </div>
            ))}
          </div>
          <div>
            <div className="font-semibold mb-1">Versements ({(a.payouts || []).length})</div>
            {(a.payouts || []).length === 0 && <div style={{ color: "var(--yalola-text-muted)" }}>Aucun versement enregistré.</div>}
            {(a.payouts || []).map((p) => (
              <div key={p.id} className="flex items-center justify-between py-0.5 border-b gap-2" style={{ borderColor: "var(--yalola-border)" }} data-testid={`associate-payout-row-${p.id}`}>
                <span>{p.date}{p.note ? ` · ${p.note}` : ""}</span>
                <span className="flex items-center gap-2"><b>{money(p.amount, p.currency || currency)}</b>
                  {!readOnly && <button type="button" title="Supprimer" onClick={() => onDeletePayout(a, p)} data-testid={`associate-payout-delete-${p.id}`}><Trash2 size={12} style={{ color: "#B91C1C" }} /></button>}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AddAssociateDialog = ({ open, onOpenChange, remaining, onAdded }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [pct, setPct] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) { setQ(""); setResults([]); setPicked(null); setPct(""); setNote(""); return undefined; }
    if (q.trim().length < 2) { setResults([]); return undefined; }
    const id = setTimeout(() => api.get(`/admin/associates/search?q=${encodeURIComponent(q.trim())}`).then(({ data }) => setResults(data.users || [])).catch(() => setResults([])), 250);
    return () => clearTimeout(id);
  }, [q, open]);
  const save = async () => {
    if (!picked) { toast.error("Choisis un membre"); return; }
    setBusy(true);
    try {
      await api.post("/admin/associates", { user_id: picked.id, pct: parseFloat(String(pct).replace(",", ".")), note });
      toast.success(`${picked.name} est maintenant associé`);
      onAdded(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Ajout impossible"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="associate-add-dialog">
        <DialogHeader><DialogTitle>Ajouter un associé</DialogTitle><DialogDescription>Recherche un membre, attribue sa part du revenu net. Il reçoit le rôle « Associé » (lecture de la Comptabilité). Parts disponibles : {pctFmt(remaining)}.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-3 opacity-50" />
            <Input className="pl-8" placeholder="Nom, e-mail ou numéro…" value={q} onChange={(e) => { setQ(e.target.value); setPicked(null); }} data-testid="associate-search-input" />
          </div>
          {!picked && results.length > 0 && (
            <div className="rounded-xl border divide-y max-h-56 overflow-auto" style={{ borderColor: "var(--yalola-border)" }} data-testid="associate-search-results">
              {results.map((u) => (
                <button key={u.id} type="button" disabled={u.already} className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-black/5 disabled:opacity-50" onClick={() => { setPicked(u); setQ(u.name || ""); }} data-testid={`associate-search-pick-${u.id}`}>
                  <Avatar className="h-7 w-7"><AvatarImage src={u.avatar_url} alt="" /><AvatarFallback className="text-[10px]">{initials(u.name)}</AvatarFallback></Avatar>
                  <span className="min-w-0 flex-1 truncate">{u.name} <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{u.email || u.phone}</span></span>
                  {u.already && <span className="chip text-[10px]">Déjà associé</span>}
                </button>
              ))}
            </div>
          )}
          {picked && <div className="text-xs" style={{ color: "#1B7A3D" }} data-testid="associate-picked">Membre choisi : <b>{picked.name}</b></div>}
          <div><Label className="text-sm font-semibold">Part (%)</Label><Input inputMode="decimal" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="ex. 10" className="mt-1" data-testid="associate-pct-input" /></div>
          <div><Label className="text-sm font-semibold">Note (optionnel)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Apport, rôle, accord…" className="mt-1" data-testid="associate-note-input" /></div>
        </div>
        <DialogFooter><Button className="pill-primary rounded-full" onClick={save} disabled={busy || !picked || !pct} data-testid="associate-add-save">{busy ? <Loader2 size={14} className="animate-spin" /> : "Ajouter"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EditPctDialog = ({ target, onOpenChange, onSaved }) => {
  const [pct, setPct] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (target) { setPct(String(target.pct ?? "")); setNote(target.note || ""); } }, [target]);
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/admin/associates/${target.id}`, { pct: parseFloat(String(pct).replace(",", ".")), note }); toast.success("Part mise à jour (effective à partir de ce mois)"); onSaved(); onOpenChange(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
    setBusy(false);
  };
  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent data-testid="associate-edit-dialog">
        <DialogHeader><DialogTitle>Modifier la part de {target?.user?.name}</DialogTitle><DialogDescription>Le nouveau % s'applique à partir du mois en cours ; les mois passés gardent leur ancien %.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-sm font-semibold">Part (%)</Label><Input inputMode="decimal" value={pct} onChange={(e) => setPct(e.target.value)} className="mt-1" data-testid="associate-edit-pct" /></div>
          <div><Label className="text-sm font-semibold">Note</Label><Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" data-testid="associate-edit-note" /></div>
        </div>
        <DialogFooter><Button className="pill-primary rounded-full" onClick={save} disabled={busy} data-testid="associate-edit-save">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PayoutDialog = ({ target, currency, onOpenChange, onSaved }) => {
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (target) { setAmount(target.balance > 0 ? String(target.balance) : ""); setNote(""); } }, [target]);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/admin/associates/${target.id}/payouts`, { amount: parseFloat(String(amount).replace(",", ".")), date, note }); toast.success("Versement enregistré"); onSaved(); onOpenChange(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
    setBusy(false);
  };
  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent data-testid="associate-payout-dialog">
        <DialogHeader><DialogTitle>Versement à {target?.user?.name}</DialogTitle><DialogDescription>Montant réellement versé (hors application) : il vient en déduction du solde à verser. Solde actuel : {money(target?.balance, currency)}.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-sm font-semibold">Montant ({currency})</Label><Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" data-testid="associate-payout-amount" /></div>
          <div><Label className="text-sm font-semibold">Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="associate-payout-date" /></div>
          <div className="col-span-2"><Label className="text-sm font-semibold">Note</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Virement, espèces, référence…" className="mt-1" data-testid="associate-payout-note" /></div>
        </div>
        <DialogFooter><Button className="pill-primary rounded-full" onClick={save} disabled={busy || !amount} data-testid="associate-payout-save">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AssociatesPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [paying, setPaying] = useState(null);
  const [showFormer, setShowFormer] = useState(false);
  const load = useCallback(() => { setLoading(true); api.get("/admin/associates").then(({ data: d }) => setData(d)).catch(() => setData(false)).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (a) => {
    if (!window.confirm(`Retirer ${a.user?.name || "ce membre"} des associés ? Sa part passe à 0 % dès ce mois et le rôle « Associé » lui est retiré.`)) return;
    try { await api.delete(`/admin/associates/${a.id}`); toast.success("Associé retiré"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const deletePayout = async (a, p) => {
    if (!window.confirm(`Supprimer le versement de ${money(p.amount, p.currency)} du ${p.date} ?`)) return;
    try { await api.delete(`/admin/associates/${a.id}/payouts/${p.id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const cur = data?.currency || "EUR";
  const active = (data?.associates || []).filter((a) => a.status === "active");
  const former = (data?.associates || []).filter((a) => a.status !== "active");
  return (
    <div className="space-y-4" data-testid="admin-associates-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}><Handshake size={20} /></div>
        <div>
          <h2 className="text-lg font-extrabold font-display">Associés</h2>
          <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Parts du revenu net de la plateforme (après frais, TVA stores, dépenses et remboursements). Gains potentiels calculés mois par mois ; les versements enregistrés donnent le solde.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" className="rounded-full" onClick={load} disabled={loading} data-testid="associates-refresh">{loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}</Button>
          <Button size="sm" className="pill-primary rounded-full" onClick={() => setAdding(true)} data-testid="associate-add-btn"><Plus size={14} className="mr-1" /> Ajouter un associé</Button>
        </div>
      </div>
      {data === false && <p className="text-sm" style={{ color: "#B91C1C" }} data-testid="associates-error">Chargement impossible.</p>}
      {data && <PoolCard data={data} />}
      {data && active.length === 0 && (
        <div className="yalola-card p-6 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="associates-empty">
          <TrendingUp size={28} className="mx-auto mb-2 opacity-50" /> Aucun associé pour l'instant. Ajoute un membre par recherche et attribue-lui sa part en %.
        </div>
      )}
      {active.map((a) => <AssociateRow key={a.id} a={a} currency={cur} onEdit={setEditing} onRemove={remove} onPayout={setPaying} onDeletePayout={deletePayout} />)}
      {former.length > 0 && (
        <div data-testid="associates-former">
          <button type="button" className="text-xs underline" style={{ color: "var(--yalola-text-muted)" }} onClick={() => setShowFormer((s) => !s)} data-testid="associates-former-toggle">{showFormer ? "Masquer" : "Afficher"} les anciens associés ({former.length})</button>
          {showFormer && former.map((a) => (
            <div key={a.id} className="yalola-card p-3 mt-2 text-sm flex items-center gap-3 flex-wrap opacity-80" data-testid={`associate-former-${a.id}`}>
              <span className="font-semibold">{a.user?.name || "Membre"}</span>
              <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{monthLabel(a.since)} → {String(a.removed_at || "").slice(0, 10)}</span>
              <span className="ml-auto text-xs">gains {money(a.earned_total, cur)} · versé {money(a.paid_total, cur)} · solde <b>{money(a.balance, cur)}</b></span>
              {a.balance > 0 && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setPaying(a)} data-testid={`associate-payout-${a.id}`}><Banknote size={13} className="mr-1" /> Versement</Button>}
              <AssociateStatementButton associateId={a.id} since={a.since} testId={`associate-statement-${a.id}`} />
            </div>
          ))}
        </div>
      )}
      <AddAssociateDialog open={adding} onOpenChange={setAdding} remaining={data?.remaining_pct ?? 100} onAdded={load} />
      <EditPctDialog target={editing} onOpenChange={(o) => !o && setEditing(null)} onSaved={load} />
      <PayoutDialog target={paying} currency={cur} onOpenChange={(o) => !o && setPaying(null)} onSaved={load} />
    </div>
  );
};

export default AssociatesPanel;
